import {
  auroraFontFamilyMetadata,
  bendFontFamilyMetadata,
  connectFontFamilyMetadata,
  defaultModeratFontFamilyMetadata,
  FontFamilyMetadata,
} from './fontFamilyMetadata'
import { baseAssets, darkModeBaseAssets, selectAssets, selectDarkModeAssets, ThemeAssets } from './theme/assets'
import { auroraAssets, auroraDarkModeAssets } from './theme/auroraAssets'
import { auroraDarkThemeColors, auroraLightThemeColors } from './theme/auroraColors'
import { auroraComponentStyles } from './theme/auroraComponentStyles'
import { bendAssets, bendDarkModeAssets } from './theme/bendAssets'
import { bendLightThemeColors } from './theme/bendColors'
import { bendComponentStyles } from './theme/bendComponentStyles'
import { darkThemeColors, lightThemeColors, ThemeColors } from './theme/colors'
import { ComponentStyles, defaultComponentStyles } from './theme/componentStyles'
import { connectDarkThemeColors, connectLightThemeColors } from './theme/connectColors'
import { lyraSelectDarkThemeColors, lyraSelectLightThemeColors } from './theme/LyraSelectColors'
import { Theme } from './theme/theme'

export enum PRODUCT_TYPE {
  WFMH = 'WFMH',
  SELECT = 'SELECT',
  CONNECT = 'CONNECT',
  WELLBEING = 'WELLBEING',
  BEND = 'BEND',
}

export const DEFAULT_PRODUCT_TYPE = PRODUCT_TYPE.WFMH

// TODO Introduce Brand to product theme structure to help scaling with theme override, currently unused
export enum ProductBrand {}

export type ThemeMetadata = {
  colorTokens: ThemeColors
  assets: ThemeAssets
  componentStyles: ComponentStyles
}

type ThemeBundle = {
  theme: {
    [Theme.DARK]: ThemeMetadata
    [Theme.LIGHT]: ThemeMetadata
  }
  fontFamilyMetadata: FontFamilyMetadata
}

type ProductThemeBundle = ThemeBundle & {
  brand?: Partial<Record<ProductBrand, ThemeBundle>>
}

type ProductTypeToThemeBundle = {
  [key in PRODUCT_TYPE]: ProductThemeBundle
}

export const productTypesToMetadata: ProductTypeToThemeBundle = {
  [PRODUCT_TYPE.WFMH]: {
    theme: {
      [Theme.DARK]: {
        colorTokens: auroraDarkThemeColors,
        assets: auroraDarkModeAssets,
        componentStyles: auroraComponentStyles,
      },
      [Theme.LIGHT]: {
        colorTokens: auroraLightThemeColors,
        assets: auroraAssets,
        componentStyles: auroraComponentStyles,
      },
    },
    fontFamilyMetadata: auroraFontFamilyMetadata,
  },
  [PRODUCT_TYPE.SELECT]: {
    theme: {
      [Theme.DARK]: {
        colorTokens: lyraSelectDarkThemeColors,
        assets: selectDarkModeAssets,
        componentStyles: defaultComponentStyles,
      },
      [Theme.LIGHT]: {
        colorTokens: lyraSelectLightThemeColors,
        assets: selectAssets,
        componentStyles: defaultComponentStyles,
      },
    },
    fontFamilyMetadata: defaultModeratFontFamilyMetadata,
  },
  [PRODUCT_TYPE.CONNECT]: {
    theme: {
      [Theme.DARK]: {
        colorTokens: connectDarkThemeColors,
        assets: auroraDarkModeAssets,
        componentStyles: auroraComponentStyles,
      },
      [Theme.LIGHT]: {
        colorTokens: connectLightThemeColors,
        assets: auroraAssets,
        componentStyles: auroraComponentStyles,
      },
    },
    fontFamilyMetadata: connectFontFamilyMetadata,
  },
  // Wellbeing shares WFMH/Aurora theme metadata until it has a dedicated product theme.
  [PRODUCT_TYPE.WELLBEING]: {
    theme: {
      [Theme.DARK]: {
        colorTokens: auroraDarkThemeColors,
        assets: auroraDarkModeAssets,
        componentStyles: auroraComponentStyles,
      },
      [Theme.LIGHT]: {
        colorTokens: auroraLightThemeColors,
        assets: auroraAssets,
        componentStyles: auroraComponentStyles,
      },
    },
    fontFamilyMetadata: auroraFontFamilyMetadata,
  },
  [PRODUCT_TYPE.BEND]: {
    theme: {
      [Theme.DARK]: {
        colorTokens: bendLightThemeColors,
        assets: bendDarkModeAssets,
        componentStyles: bendComponentStyles,
      },
      [Theme.LIGHT]: {
        colorTokens: bendLightThemeColors,
        assets: bendAssets,
        componentStyles: bendComponentStyles,
      },
    },
    fontFamilyMetadata: bendFontFamilyMetadata,
  },
}

const legacyProductTypesToMetadata: ProductTypeToThemeBundle = {
  ...productTypesToMetadata,
  [PRODUCT_TYPE.WFMH]: {
    theme: {
      [Theme.DARK]: {
        colorTokens: darkThemeColors,
        assets: darkModeBaseAssets,
        componentStyles: defaultComponentStyles,
      },
      [Theme.LIGHT]: {
        colorTokens: lightThemeColors,
        assets: baseAssets,
        componentStyles: defaultComponentStyles,
      },
    },
    fontFamilyMetadata: defaultModeratFontFamilyMetadata,
  },
  // Wellbeing shares WFMH theme metadata until it has a dedicated product theme.
  [PRODUCT_TYPE.WELLBEING]: {
    theme: {
      [Theme.DARK]: {
        colorTokens: darkThemeColors,
        assets: darkModeBaseAssets,
        componentStyles: defaultComponentStyles,
      },
      [Theme.LIGHT]: {
        colorTokens: lightThemeColors,
        assets: baseAssets,
        componentStyles: defaultComponentStyles,
      },
    },
    fontFamilyMetadata: defaultModeratFontFamilyMetadata,
  },
}

export const getProductTypeToMetadata = (forceNonAurora = false): ProductTypeToThemeBundle =>
  forceNonAurora ? legacyProductTypesToMetadata : productTypesToMetadata
